import { apiClient } from './api';
import type {
  InquirySummary,
  InquiryStatus,
  InquiryActionResponse,
  InquiryFilterParams,
  InquiryPageParams,
  PagedResponse,
  ReplyToInquiryRequest,
} from '../types/inquiry';

/** Maximum allowed length for a reply message (enforced client-side and by backend). */
export const MAX_REPLY_LENGTH = 2000;

/** Default number of inquiries returned per page. */
export const INQUIRIES_DEFAULT_PAGE_SIZE = 20;

/**
 * Raw shape returned by the backend — status is UPPERCASE.
 * Normalised to lowercase before returning to the application.
 */
type RawInquirySummary = Omit<InquirySummary, 'status'> & { status: string };
type RawInquiryActionResponse = Omit<InquiryActionResponse, 'status'> & { status: string };

const normalizeInquiry = (raw: RawInquirySummary): InquirySummary => ({
  ...raw,
  name: raw.name ?? null,
  customerId: raw.customerId ?? null,
  replyText: raw.replyText ?? null,
  status: raw.status.toLowerCase() as InquiryStatus,
});

const normalizeAction = (raw: RawInquiryActionResponse): InquiryActionResponse => ({
  ...raw,
  replyText: raw.replyText ?? null,
  status: raw.status.toLowerCase() as InquiryStatus,
});

export const inquiryService = {
  /**
   * GET /api/v1/admin/inquiries
   * Returns a paginated, filterable list of all contact inquiries.
   * The status filter param is sent UPPERCASE to match the backend enum.
   */
  async listInquiries(
    filter: InquiryFilterParams = {},
    pageParams: InquiryPageParams = {},
  ): Promise<PagedResponse<InquirySummary>> {
    const params = {
      ...(filter.status ? { status: filter.status.toUpperCase() } : {}),
      ...(filter.submitterEmail ? { submitterEmail: filter.submitterEmail } : {}),
      ...(filter.createdAtFrom ? { createdAtFrom: filter.createdAtFrom } : {}),
      ...(filter.createdAtTo ? { createdAtTo: filter.createdAtTo } : {}),
      page: pageParams.page ?? 0,
      size: pageParams.size ?? INQUIRIES_DEFAULT_PAGE_SIZE,
    };
    const response = await apiClient.get<PagedResponse<RawInquirySummary>>(
      '/admin/inquiries',
      { params },
    );
    return {
      ...response.data,
      content: response.data.content.map(normalizeInquiry),
    };
  },

  /**
   * PATCH /api/v1/admin/inquiries/{id}/read
   * Transitions status to READ. No-op if already REPLIED (backend returns 200 unchanged).
   */
  async markAsRead(inquiryId: string): Promise<InquiryActionResponse> {
    const response = await apiClient.patch<RawInquiryActionResponse>(
      `/admin/inquiries/${inquiryId}/read`,
    );
    return normalizeAction(response.data);
  },

  /**
   * POST /api/v1/admin/inquiries/{id}/reply
   * Sends an email reply and transitions status to REPLIED.
   */
  async replyToInquiry(
    inquiryId: string,
    request: ReplyToInquiryRequest,
  ): Promise<InquiryActionResponse> {
    const response = await apiClient.post<RawInquiryActionResponse>(
      `/admin/inquiries/${inquiryId}/reply`,
      request,
    );
    return normalizeAction(response.data);
  },

  /**
   * PATCH /api/v1/admin/inquiries/{id}/replied
   * Transitions status to REPLIED without sending an email.
   */
  async markAsReplied(inquiryId: string): Promise<InquiryActionResponse> {
    const response = await apiClient.patch<RawInquiryActionResponse>(
      `/admin/inquiries/${inquiryId}/replied`,
    );
    return normalizeAction(response.data);
  },
};
